import { Injectable } from '@angular/core';

import { formatDate } from '@vendasta/galaxy/utility/date-utils';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map, publishReplay, refCount, shareReplay, tap } from 'rxjs/operators';
import { LocalStorageService } from '@vendasta/uikit';
import { getDateBoundaries } from '../../core/metrics.helper';

export interface DateRangeOption {
  key: string;
  name: string;
  i18nName?: string;
  dates?: string;
  // If provided, the date range which that option represents
  dateRange?: [Date, Date];
}

export const AvailableDateRangeOptions: DateRangeOption[] = [
  {
    key: '7days',
    name: 'Last 7 days',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_7_DAYS',
  },
  {
    key: '30days',
    name: 'Last 30 days',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_30_DAYS',
  },
  {
    key: '90days',
    name: 'Last 90 days',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_90_DAYS',
  },
  {
    key: '6months',
    name: 'Last 6 months',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_6_MONTHS',
  },
  {
    key: '12months',
    name: 'Last 12 months',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_12_MONTHS',
  },
  {
    key: '16months',
    name: 'Last 16 months',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_16_MONTHS',
  },
  {
    key: 'custom',
    name: 'Custom',
    i18nName: 'ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.CUSTOM',
  },
];

@Injectable({
  providedIn: 'root',
})
export class DateRangeSelectorService {
  public today: Date;
  timeRangeOptions = AvailableDateRangeOptions;
  public timeRange$: Observable<string>;
  public dateRange$: Observable<[Date, Date]>; // these are local aligned calendar concepts (start of day/month in local)
  public utcAlignedDateRange$: Observable<[Date, Date]>; // utc aligned version of dateRange$
  public startDate$: Observable<Date>;
  public endDate$: Observable<Date>;
  private timeRange$$ = new BehaviorSubject('12months');
  private dateRange$$: ReplaySubject<[Date, Date]> = new ReplaySubject(1);

  constructor(private localStorage: LocalStorageService) {
    this.today = new Date();
    // Add human-readable date ranges to the preset options
    this.timeRangeOptions.forEach((o) => {
      if (o.key !== 'custom') {
        const [startDate, endDate] = getDateBoundaries(o.key);
        const start = formatDate(startDate, 'MMM do, yyyy');
        const end = formatDate(endDate, 'MMM do, yyyy');
        o.dates = `(${start} - ${end})`;
        // The effective date range is recomputed when the preset is selected, this may be used
        // when switching from a preset to a custom range
        o.dateRange = [startDate, endDate];
      }
    });

    // Load stored preset
    const timeRangePreset = localStorage.get('timeRangePreset');
    // Make sure the key exists, if not, set the key to the default
    if (timeRangePreset && !!AvailableDateRangeOptions.find((tr) => tr.key === timeRangePreset)) {
      if (timeRangePreset === 'custom') {
        const startISO = this.localStorage.get('customDateRangeStart');
        const endISO = this.localStorage.get('customDateRangeEnd');
        if (startISO && endISO) {
          // Set the custom option's range
          this.setDateRangeDirect([new Date(startISO), new Date(endISO)]);
        }
      } else {
        this.setTimeRangeDirect(timeRangePreset);
      }
    } else {
      this.setTimeRange('12months');
    }

    this.timeRange$ = this.timeRange$$.asObservable();
    this.dateRange$ = this.dateRange$$.asObservable().pipe(
      tap((dr) => {
        if (this.timeRange$$.getValue() === 'custom') {
          // Update time range option of custom value
          this.timeRangeOptions.find((tro) => tro.key === 'custom').dateRange = dr;
        } else {
          this.timeRangeOptions.find((tro) => tro.key === 'custom').dateRange = null;
        }
      }),
      publishReplay(1),
      refCount(),
    );
    this.utcAlignedDateRange$ = this.dateRange$.pipe(
      map(([startDate, endDate]: [Date, Date]): [Date, Date] => {
        return [this.alignLocalDateToUTC(startDate), this.alignLocalDateToUTC(endDate)];
      }),
      shareReplay(1),
    );

    this.startDate$ = this.dateRange$.pipe(
      map((tr) => tr[0]),
      shareReplay(1),
    );
    this.endDate$ = this.dateRange$.pipe(
      map((tr) => tr[1]),
      shareReplay(1),
    );
  }

  public setTimeRange(tr: string): void {
    this.setTimeRangeDirect(tr);
    this.localStorage.set('timeRangePreset', tr);
  }

  public setTimeRangeDirect(tr: string): void {
    this.timeRange$$.next(tr);
    const opt = this.timeRangeOptions.find((timeRange) => timeRange.key === tr);
    if (opt.key === 'custom') {
      // Use the custom
      this.dateRange$$.next(opt.dateRange);
    } else {
      // Derive the range from preset and now
      const [startDate, endDate] = getDateBoundaries(opt.key);
      // Update the value on the option so that it sets on the way to custom?
      opt.dateRange = [startDate, endDate];
      this.dateRange$$.next(opt.dateRange);
    }
  }

  public setDateRange(dr: [Date, Date]): void {
    this.setDateRangeDirect(dr);
    this.localStorage.set('timeRangePreset', 'custom');
    this.localStorage.set('customDateRangeStart', dr[0].toISOString());
    this.localStorage.set('customDateRangeEnd', dr[1].toISOString());
  }

  public setDateRangeDirect(dr: [Date, Date]): void {
    const opt = this.timeRangeOptions.find((timeRange) => timeRange.key === 'custom');
    const start = formatDate(dr[0], 'MMM do, yyyy');
    const end = formatDate(dr[1], 'MMM do, yyyy');
    opt.dates = `(${start} - ${end})`;
    opt.dateRange = dr;
    this.timeRange$$.next('custom');
    this.dateRange$$.next(dr);
  }

  alignLocalDateToUTC(now: Date): Date {
    if (!now) {
      return now;
    }
    return new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()),
    );
  }
}
